import {
  Component,
  Input,
  Output,
  EventEmitter,
  HostListener,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { formatVehicleLine } from '../../shared/utils/format-vehicle-line.util';

export interface VehicleItemData {
  id: string | number;
  year: number;
  make: string;
  model: string;
  licensePlate: string;
  vin: string;
  selected?: boolean;
}

/**
 * Represents a single vehicle list item with display and action controls (select, edit, delete) and built-in mobile menu handling.
 * @component
 * @selector app-vehicle-item
 * @example
 *   <app-vehicle-item
 *     [vehicleData]="vehicle"
 *     [isSelected]="false"
 *     [showActions]="true"
 *     [isMenuOpen]="false"
 *     (selectVehicle)="onSelectVehicle($event)"
 *     (editVehicle)="onEditVehicle($event)"
 *     (deleteVehicle)="onDeleteVehicle($event)"
 *     (toggleMenu)="onToggleMenu($event)">
 *   </app-vehicle-item>
 * @input {VehicleItemData} vehicleData - Required object containing vehicle details (e.g., year, make, model) to display.
 * @input {boolean} isSelected - Flag indicating whether this vehicle item is in a selected state.
 * @input {boolean} showActions - Controls whether action buttons (edit/delete/menu) are shown.
 * @input {boolean} isMenuOpen - External indicator for whether the item's menu is open.
 * @output {EventEmitter<VehicleItemData>} selectVehicle - Emitted when the item is selected (via onSelectVehicle()).
 * @output {EventEmitter<VehicleItemData>} editVehicle - Emitted to request editing this vehicle (via onEditVehicle() or onMobileEdit()).
 * @output {EventEmitter<VehicleItemData>} deleteVehicle - Emitted to request deletion of this vehicle (via onDeleteVehicle() or onMobileDelete()).
 * @output {EventEmitter<VehicleItemData>} toggleMenu - Emitted when the menu toggle is activated (via toggleMobileMenu()).
 *
 * Key methods:
 * - onSelectVehicle(): Emits selectVehicle with vehicleData when the item is clicked.
 * - onEditVehicle(event: Event): Stops event propagation and emits editVehicle for desktop edit actions.
 * - onDeleteVehicle(event: Event): Stops event propagation and emits deleteVehicle for desktop delete actions.
 * - toggleMobileMenu(event: Event): Stops propagation and emits toggleMenu to request toggling the menu (mobile).
 * - onMobileEdit(): Emits editVehicle for mobile menu edit action.
 * - onMobileDelete(): Emits deleteVehicle for mobile menu delete action.
 * - get vehicleName(): string - Convenience getter that returns a formatted display name ("{year} {make} {model}").
 *
 * Lifecycle and behavior:
 * - Uses a HostListener on document clicks (onDocumentClick) to detect outside clicks and close the internal mobile menu (showMobileMenu).
 * - Maintains internal showMobileMenu state for mobile menu visibility while allowing external control via isMenuOpen and toggleMenu output.
 */
@Component({
  selector: 'app-vehicle-item',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './vehicle-item.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./vehicle-item.component.scss'],
})
export class VehicleItemComponent {
  @Input() vehicleData!: VehicleItemData;
  @Input() isSelected = false;
  @Input() showActions = true;
  @Input() isMenuOpen = false;

  @Output() selectVehicle = new EventEmitter<VehicleItemData>();
  @Output() editVehicle = new EventEmitter<VehicleItemData>();
  @Output() deleteVehicle = new EventEmitter<VehicleItemData>();
  @Output() toggleMenu = new EventEmitter<VehicleItemData>();

  // Mobile menu state
  showMobileMenu = false;

  onSelectVehicle(): void {
    this.selectVehicle.emit(this.vehicleData);
  }

  /** Select when clicking anywhere on the card except action buttons / mobile menu. */
  onCardClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('.vehicle-actions')) {
      return;
    }
    this.onSelectVehicle();
  }

  onCardKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      this.onSelectVehicle();
    }
  }

  onEditVehicle(event: Event): void {
    event.stopPropagation();
    this.editVehicle.emit(this.vehicleData);
  }

  onDeleteVehicle(event: Event): void {
    event.stopPropagation();
    this.deleteVehicle.emit(this.vehicleData);
  }

  get vehicleName(): string {
    return formatVehicleLine(this.vehicleData.year, this.vehicleData.make, this.vehicleData.model);
  }

  // Mobile menu methods
  toggleMobileMenu(event: Event): void {
    event.stopPropagation();
    this.toggleMenu.emit(this.vehicleData);
  }

  onMobileEdit(): void {
    this.editVehicle.emit(this.vehicleData);
  }

  onMobileDelete(): void {
    this.deleteVehicle.emit(this.vehicleData);
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.showMobileMenu) {
      this.showMobileMenu = false;
    }
  }
}
